// src/constants/api.ts
var API_ENDPOINTS = {
  // Call tracing — per-system call timeline (see types/telemetry.ts)
  // Device single-event report (DeviceToken auth). Lives under /device/* rather
  // than /calls/* because the API's Asterisk router gates ALL /calls/* paths
  // behind the shared secret. Body carries the CallTraceEvent, so the messy
  // correlation id (SIP Call-ID / linkedid) never has to be URL-encoded.
  // (Asterisk reports its own steps to /api/v1/calls/event with the secret.)
  CALL_EVENT_REPORT: "/api/v1/device/call-event"};

// src/telemetry/CallTracer.ts
var CallTracer = class {
  sink;
  defaultSource;
  constructor(sink, options = {}) {
    this.sink = sink;
    this.defaultSource = options.defaultSource ?? "device";
  }
  /**
   * Report a step. Returns immediately; the POST runs in the background.
   *
   * @param callId correlation key — the SIP Call-ID the device observes, or the
   *   X-Caro-Call-Id (Asterisk linkedid) header value when present.
   */
  emit(callId, step, opts = {}) {
    if (!callId) {
      console.warn("[CallTracer] Dropping event with no callId:", step);
      return;
    }
    const event = {
      callId,
      step,
      source: opts.source ?? this.defaultSource,
      level: opts.level ?? "info",
      at: (/* @__PURE__ */ new Date()).toISOString(),
      ...opts.detail ? { detail: opts.detail } : {}
    };
    console.log(`[CallTracer] ${event.source}:${step}`, callId, opts.detail ?? "");
    void this.sink.post(API_ENDPOINTS.CALL_EVENT_REPORT, event).catch((err) => {
      console.warn(`[CallTracer] Failed to report ${step}:`, err?.message ?? err);
    });
  }
};

// src/telemetry/ConnectivityMonitor.ts
var LAYER_KEYS = ["network", "internet", "api", "sip"];
var ConnectivityMonitor = class {
  deps;
  now;
  checkIntervalMs;
  keepaliveMs;
  maxBuffer;
  flushPageSize;
  retryBaseMs;
  timer = null;
  buffer = [];
  lastFlushAt = null;
  lastFlushedLayers = null;
  running = false;
  loaded = false;
  checking = false;
  // Failed-flush backoff (in memory — a restart retries at once, which is what we want).
  failures = 0;
  nextAttemptAt = 0;
  failedLayers = null;
  // A successful page left older samples behind: send the next page on the next tick.
  backlog = false;
  constructor(deps, options = {}) {
    this.deps = deps;
    this.now = deps.now ?? (() => Date.now());
    this.checkIntervalMs = options.checkIntervalMs ?? 6e4;
    this.keepaliveMs = options.keepaliveMs ?? 9e5;
    this.maxBuffer = options.maxBuffer ?? 240;
    this.flushPageSize = Math.max(1, options.flushPageSize ?? 60);
    this.retryBaseMs = options.retryBaseMs ?? 6e4;
  }
  /** Begin sampling. Rehydrates any buffered samples from a prior run first. */
  async start() {
    if (this.running) return;
    this.running = true;
    await this.loadState();
    await this.check();
    this.timer = setInterval(() => {
      void this.check();
    }, this.checkIntervalMs);
  }
  stop() {
    this.running = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
  /** Take one sample, buffer it, and flush if state changed or keepalive due. */
  async check() {
    if (this.checking) return;
    this.checking = true;
    try {
      const reading = await this.deps.probe();
      const sample = { ...reading, at: new Date(this.now()).toISOString() };
      this.buffer.push(sample);
      if (this.buffer.length > this.maxBuffer) {
        const dropped = this.buffer.length - this.maxBuffer;
        this.buffer.splice(0, dropped);
        console.warn(`[Connectivity] Buffer cap reached \u2014 dropped ${dropped} oldest sample(s)`);
      }
      await this.persist();
      const changed = this.didChange(sample);
      const keepaliveDue = this.lastFlushAt === null || this.now() - this.lastFlushAt >= this.keepaliveMs;
      if ((changed || keepaliveDue || this.backlog) && this.mayAttempt(sample)) {
        await this.doFlush(changed ? "change" : "keepalive", sample);
      }
    } catch (err) {
      console.warn("[Connectivity] check failed:", err?.message ?? err);
    } finally {
      this.checking = false;
    }
  }
  /** True when any monitored layer flipped vs. the last successfully-flushed state. */
  didChange(sample) {
    if (!this.lastFlushedLayers) return true;
    return LAYER_KEYS.some((k) => this.lastFlushedLayers[k] !== sample[k]);
  }
  /**
   * Backoff after a failed flush. Without it a failure left `lastFlushAt` stale and the
   * layers "changed", so the tablet re-posted every check (60 s) forever. A layer flip
   * that is new since the failed attempt still goes out at once — that is the signal.
   */
  mayAttempt(sample) {
    if (this.failures === 0 || this.now() >= this.nextAttemptAt) return true;
    return !!this.failedLayers && LAYER_KEYS.some((k) => this.failedLayers[k] !== sample[k]);
  }
  async doFlush(reason, current) {
    const page = this.buffer.slice(0, this.flushPageSize);
    const heartbeat = {
      reason,
      current,
      samples: page
    };
    try {
      await this.deps.flush(heartbeat);
      this.buffer.splice(0, page.length);
      this.backlog = this.buffer.length > 0;
      this.failures = 0;
      this.nextAttemptAt = 0;
      this.failedLayers = null;
      this.lastFlushAt = this.now();
      this.lastFlushedLayers = {
        network: current.network,
        internet: current.internet,
        api: current.api,
        sip: current.sip
      };
      await this.persist();
    } catch (err) {
      this.failures += 1;
      const delay = Math.min(this.retryBaseMs * 2 ** (this.failures - 1), this.keepaliveMs);
      this.nextAttemptAt = this.now() + delay;
      this.failedLayers = {
        network: current.network,
        internet: current.internet,
        api: current.api,
        sip: current.sip
      };
      console.warn(`[Connectivity] flush (${reason}) failed \u2014 retaining ${this.buffer.length} sample(s), retry in ${Math.round(delay / 1e3)}s:`, err?.message ?? err);
    }
  }
  async loadState() {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const raw = await this.deps.storage.read();
      if (!raw) return;
      const state = JSON.parse(raw);
      if (Array.isArray(state.samples)) this.buffer = state.samples.slice(-this.maxBuffer);
      this.lastFlushAt = state.lastFlushAt ? new Date(state.lastFlushAt).getTime() : null;
      this.lastFlushedLayers = state.lastFlushedLayers ?? null;
    } catch (err) {
      console.warn("[Connectivity] Failed to load persisted buffer:", err?.message ?? err);
    }
  }
  async persist() {
    const state = {
      samples: this.buffer,
      lastFlushAt: this.lastFlushAt ? new Date(this.lastFlushAt).toISOString() : null,
      lastFlushedLayers: this.lastFlushedLayers
    };
    try {
      await this.deps.storage.write(JSON.stringify(state));
    } catch (err) {
      console.warn("[Connectivity] Failed to persist buffer:", err?.message ?? err);
    }
  }
};

// src/telemetry/battery.ts
var INT_MIN = -2147483648;
function normalizeCurrentMa(raw) {
  if (raw == null || raw === INT_MIN || raw === 0 || !Number.isFinite(raw)) return null;
  const abs = Math.abs(raw);
  if (abs < 1e4) return { ma: abs, unit: "mA" };
  return { ma: abs / 1e3, unit: "\xB5A" };
}
var BATTERY_DRAIN_WINDOW_MS = 60 * 6e4;
function computeBatteryDrain(points, now, windowMs = BATTERY_DRAIN_WINDOW_MS) {
  if (points.length < 2) return null;
  const last = points[points.length - 1];
  if (last.plugged) return null;
  let first = last;
  for (let i = points.length - 2; i >= 0; i--) {
    const p = points[i];
    if (p.plugged || now - p.t > windowMs) break;
    first = p;
  }
  const hours = (last.t - first.t) / 36e5;
  if (hours < 10 / 60) return null;
  let pct;
  if (first.uah != null && last.uah != null && first.uah > 0 && last.uah > 0 && last.level > 0) {
    const fullUah = last.uah / (last.level / 100);
    pct = (first.uah - last.uah) / fullUah * 100;
  } else {
    pct = first.level - last.level;
  }
  return { pctPerHour: pct / hours, windowMin: Math.round(hours * 60) };
}

export { BATTERY_DRAIN_WINDOW_MS, CallTracer, ConnectivityMonitor, computeBatteryDrain, normalizeCurrentMa };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map